import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { dashboardService, workflowService, tagService, DashboardFilters } from '../services';
import { Dashboard, Workflow, Tag } from '../types';
import { ALL_WIDGET_DEFS, getWidgetLabel } from '../components/widgets/registry';
import { useThrottle } from '../hooks';
import ImportModal from '../components/common/ImportModal';
import TagBadge from '../components/common/TagBadge';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import PageTopBar from '../components/ui/PageTopBar';
import DescriptionCells from '../components/common/ExpandableDescription';

// Icons as components for cleaner code
const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const FilterIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
    />
  </svg>
);

const UploadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
    />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zm0 6a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1v-7zm-10 2a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5z"
    />
  </svg>
);

const WidgetIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
    />
  </svg>
);

const WorkflowIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const EmptyIcon = () => (
  <svg className="w-16 h-16 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1}
      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
  </svg>
);

const WIDGET_TYPE_OPTIONS = ALL_WIDGET_DEFS
  .filter(d => d.meta.isCreatable)
  .map(d => ({ value: d.type, label: getWidgetLabel(d.type) }));

// Helper to count how many workflows use a dashboard
const countWorkflowsUsingDashboard = (dashboardId: string, workflows: Workflow[]): number => {
  let count = 0;
  for (const workflow of workflows) {
    const nodes = workflow.flowData?.nodes || [];
    for (const node of nodes) {
      if ((node.data as Record<string, unknown>)?.dashboardId === dashboardId) {
        count++;
        break; // Count each workflow only once
      }
    }
  }
  return count;
};

const Dashboards: React.FC = () => {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');
  const [selectedWidgetType, setSelectedWidgetType] = useState<string>('');
  const [selectedTagId, setSelectedTagId] = useState<string | undefined>(undefined);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedDescId, setExpandedDescId] = useState<string | null>(null);
  const throttledSearch = useThrottle(searchQuery);

  // Sort state
  type DashSortField = 'name' | 'widgets' | 'workflows' | 'tags' | 'updated';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<DashSortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: DashSortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'updated' ? 'desc' : 'asc');
    }
  };

  // Load workflows + tag pool for the filter dropdowns
  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        const response = await workflowService.getWorkflows(1, 100);
        setWorkflows(response.data);
      } catch (err) {
        console.error('Failed to load workflows:', err);
      }
    };
    const loadTags = async () => {
      try {
        const data = await tagService.list();
        setTags(data);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    };
    loadWorkflows();
    loadTags();
  }, []);

  const loadDashboards = useCallback(async () => {
    try {
      setLoading(true);
      const filters: DashboardFilters = {};
      if (throttledSearch) filters.search = throttledSearch;
      if (selectedWorkflow) filters.workflowId = selectedWorkflow;
      if (selectedWidgetType) filters.widgetType = selectedWidgetType;
      if (selectedTagId) filters.tagId = selectedTagId;

      const response = await dashboardService.getDashboards(page, 12, filters);
      setDashboards(response.data);
      setTotalPages(Math.ceil(response.total / response.pageSize));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  }, [page, throttledSearch, selectedWorkflow, selectedWidgetType, selectedTagId]);

  useEffect(() => {
    loadDashboards();
  }, [loadDashboards]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [throttledSearch, selectedWorkflow, selectedWidgetType, selectedTagId]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedWorkflow('');
    setSelectedWidgetType('');
    setSelectedTagId(undefined);
  };

  const hasActiveFilters =
    searchQuery || selectedWorkflow || selectedWidgetType || selectedTagId;

  const handleCreate = () => {
    navigate('/dashboard-builder');
  };

  const handleEdit = (id: string) => {
    navigate(`/dashboard-builder/${id}`);
  };

  const handleImportComplete = () => {
    setIsImportModalOpen(false);
    loadDashboards();
  };

  // Format relative date for cleaner display
  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Sort dashboards
  const sortedDashboards = useMemo(() => {
    const sorted = [...dashboards];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'widgets':
          cmp = a.widgets.length - b.widgets.length;
          break;
        case 'workflows':
          cmp = countWorkflowsUsingDashboard(a.id, workflows) - countWorkflowsUsingDashboard(b.id, workflows);
          break;
        case 'tags':
          cmp = (a.tags?.length ?? 0) - (b.tags?.length ?? 0);
          break;
        case 'updated':
          cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [dashboards, sortField, sortDir, workflows]);

  const SortHeader: React.FC<{ field: DashSortField; label: string; className?: string }> = ({ field, label, className = '' }) => (
    <th
      className={`px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer select-none hover:text-neutral-700 transition-colors ${className}`}
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        <FontAwesomeIcon
          icon={sortField === field ? (sortDir === 'asc' ? faSortUp : faSortDown) : faSort}
          className={`w-3 h-3 ${sortField === field ? 'text-primary-500' : 'text-neutral-300'}`}
        />
      </span>
    </th>
  );

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar
        title="Dashboard Library"
        description="Compose widgets into dashboards for workflow steps"
      />
      <div className="flex-1 overflow-y-auto">
      <div className="w-full px-6 lg:px-8 py-8">

        {/* Search and Filters */}
        <div className="mb-6 space-y-3">
          {/* Search Bar Row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[280px] max-w-md">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
                <SearchIcon />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search dashboards..."
                className="w-full h-9 px-4 pl-10 text-sm bg-surface border border-neutral-200 rounded-lg
                         placeholder:text-neutral-400
                        
                         transition-all duration-fast"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Tag filter */}
            <select
              value={selectedTagId ?? ''}
              onChange={e => setSelectedTagId(e.target.value || undefined)}
              className="h-9 px-3 text-sm bg-surface border border-neutral-200 rounded-lg w-48"
            >
              <option value="">All tags</option>
              {tags.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`h-9 px-3 text-sm font-medium border rounded-lg flex items-center gap-2 transition-all duration-fast ${
                showFilters || hasActiveFilters
                  ? 'bg-primary-50 border-primary-200 text-primary-700'
                  : 'bg-surface border-neutral-200 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300'
              }`}
            >
              <FilterIcon />
              <span>Filters</span>
              {hasActiveFilters && (
                <span className="ml-1 bg-primary-600 text-white text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {[selectedWorkflow, selectedWidgetType, selectedTagId].filter(Boolean).length +
                    (searchQuery ? 1 : 0)}
                </span>
              )}
            </button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            )}

            {/* Primary actions — pushed to the right */}
            <div className="ml-auto flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsImportModalOpen(true)}
              >
                Import
              </Button>
              {dashboards.length > 0 && (
                <Button variant="primary" size="sm" onClick={handleCreate}>
                  Create Dashboard
                </Button>
              )}
            </div>
          </div>

          {/* Expanded Filter Panel */}
          {showFilters && (
            <Card variant="default" className="animate-fade-in">
              <CardBody className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px] max-w-xs">
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">
                    In Workflow
                  </label>
                  <select
                    value={selectedWorkflow}
                    onChange={e => setSelectedWorkflow(e.target.value)}
                    className="w-full h-9 px-3 text-sm bg-surface border border-neutral-200 rounded-lg
                            
                             transition-all duration-fast"
                  >
                    <option value="">All workflows</option>
                    {workflows.map(workflow => (
                      <option key={workflow.id} value={workflow.id}>
                        {workflow.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 min-w-[200px] max-w-xs">
                  <label className="block text-xs font-medium text-neutral-500 mb-1.5">
                    Contains Widget Type
                  </label>
                  <select
                    value={selectedWidgetType}
                    onChange={e => setSelectedWidgetType(e.target.value)}
                    className="w-full h-9 px-3 text-sm bg-surface border border-neutral-200 rounded-lg
                            
                             transition-all duration-fast"
                  >
                    <option value="">All widget types</option>
                    {WIDGET_TYPE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center justify-between animate-fade-in">
            <span className="text-sm text-danger-700">{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-danger-500 hover:text-danger-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col justify-center items-center h-64 gap-3">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            <span className="text-sm text-neutral-500">Loading dashboards...</span>
          </div>
        ) : dashboards.length === 0 ? (
          /* Empty State */
          <Card variant="default" className="text-center py-16">
            <CardBody className="flex flex-col items-center">
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                {hasActiveFilters ? 'No matching dashboards' : 'No dashboards yet'}
              </h3>
              <p className="text-sm text-neutral-500 mb-6 max-w-sm">
                {hasActiveFilters
                  ? "Try adjusting your search or filters to find what you're looking for"
                  : 'Get started by creating a dashboard to organize your widgets'}
              </p>
              {hasActiveFilters ? (
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : (
                <Button variant="primary" size="sm" onClick={handleCreate}>
                  Create Dashboard
                </Button>
              )}
            </CardBody>
          </Card>
        ) : (
          /* Dashboard Grid */
          <Card variant="default">
            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-50 border-y border-neutral-100">
                    <SortHeader field="name" label="Dashboard" />
                    <SortHeader field="widgets" label="Widgets" />
                    <SortHeader field="workflows" label="Workflows" />
                    <SortHeader field="tags" label="Tags" className="hidden md:table-cell" />
                    <SortHeader field="updated" label="Updated" />
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden lg:table-cell">Description</th>
                    <th className="px-6 py-3 hidden lg:table-cell w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {sortedDashboards.map(dashboard => {
                    const workflowCount = countWorkflowsUsingDashboard(dashboard.id, workflows);
                    return (
                      <React.Fragment key={dashboard.id}>
                      <tr
                        className="hover:bg-neutral-50 transition-colors cursor-pointer group"
                        onClick={() => handleEdit(dashboard.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-neutral-900 group-hover:text-primary-700 transition-colors">
                            {dashboard.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                            <WidgetIcon />
                            {dashboard.widgets.length}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                            <WorkflowIcon />
                            {workflowCount}
                          </span>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell">
                          {dashboard.tags && dashboard.tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {dashboard.tags.slice(0, 3).map(tag => (
                                <TagBadge key={tag.id} tag={tag} />
                              ))}
                              {dashboard.tags.length > 3 && (
                                <Badge size="sm" variant="default">+{dashboard.tags.length - 3}</Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-neutral-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-xs text-neutral-400">{formatRelativeDate(dashboard.updatedAt)}</span>
                        </td>
                        <DescriptionCells
                          text={dashboard.description}
                          expanded={expandedDescId === dashboard.id}
                          onToggle={(e) => { e.stopPropagation(); setExpandedDescId(expandedDescId === dashboard.id ? null : dashboard.id); }}
                        />
                      </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="mt-8 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              icon={<ChevronLeftIcon />}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1 px-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                    pageNum === page
                      ? 'bg-primary-600 text-white'
                      : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              icon={<ChevronRightIcon />}
              iconPosition="right"
            >
              Next
            </Button>
          </nav>
        )}
      </div>
      </div>

      {/* Import Modal */}
      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={handleImportComplete}
        allowedTypes={['dashboard']}
      />
    </div>
  );
};

export default Dashboards;
